import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'

import { Subtitle1 } from '@src/elements/typography/typography'
import { Icon } from '@src/elements/icon/icon'
import Member from '@src/data/member'
import { API } from '@src/lib/api_path'
import { HealthGoal } from '@src/data/goal'
import ApiClient, { ApiCallError } from '@src/lib/api_client'
import { BodyMeasurements } from '@src/data/body-measurement'
import EventBus from '@src/lib/event_bus'

import './details-targets.sass'
import { convertLengthForDisplaying, convertWeightForDisplaying, LengthUnit, WeightUnit } from '@src/util/weight'

type HealthGoalResponse = { health_goal: HealthGoal }
type BodyMeasurementsResponse = { profile: BodyMeasurements }

@Component({
  computed: {
    weightUnit () {
      return this.$store.state.club.weightUnit.toUpperCase()
    },
    locale () {
      return this.$store.state.i18n.locale
    },
    heightUnit () {
      return this.$store.state.club.weightUnit === WeightUnit.KG ? LengthUnit.CM : LengthUnit.IN
    }
  }
})
export class DetailsAndTargets extends Vue {

  @Prop({ required: true })
  member: Member

  weight: number | null = null
  height: number | null = null

  protected weightUnit: WeightUnit
  protected heightUnit: LengthUnit
  protected apiClient: ApiClient = new ApiClient()
  protected goal: any = ''

  constructor () {
    super()
  }

  @Watch('locale')
  async updateTitleOnLanguageChange () {
    await this.loadCurrentGoal()
    this.$forceUpdate()
  }

  render (h: CreateElement) {
    const metTarget = this.member.currentActivityGoal && this.member.currentActivityGoal.value >= 0 ? `${this.member.currentActivityGoal.completed}/${this.member.currentActivityGoal.value}` : '-'

    return <div class='details-targets'>
      {this.renderInfo(h, 'brand/lifestyle', this.$t('PAGE.MEMBERS.STATUS.LIFESTYLE'), this.member.activityLevel ? this.$t(`PAGE.MEMBERS.STATUS.LIFESTYLE_TYPE.${this.member.activityLevel.toUpperCase()}`) : '-')}
      {this.renderInfo(h, 'brand/fitness-level', this.$t('PAGE.MEMBERS.STATUS.FITNESS_LEVEL'), this.member.expertiseLevel ? this.$t(`PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.${this.member.expertiseLevel.toUpperCase()}`) : '-')}
      {this.renderInfo(h, 'brand/met-target', this.$t('PAGE.MEMBERS.STATUS.MET_TARGET'), metTarget)}
      {this.renderInfo(h, 'brand/goal', this.$t('PAGE.MEMBERS.STATUS.GOAL'), this.goal || '-')}
      {this.renderInfo(h, 'brand/age', this.$t('PAGE.MEMBERS.STATUS.AGE'), `${this.member.age} ${this.$t('YEARS')}`)}
      {this.renderInfo(h, 'brand/height', this.$t('PAGE.MEMBERS.STATUS.HEIGHT'), `${this.height ? convertLengthForDisplaying(this.height, this.heightUnit) : '-'} ${this.$t(`UNITS.${this.heightUnit.toUpperCase()}`)}`)}
      {this.renderInfo(h, 'brand/weight', this.$t('PAGE.MEMBERS.STATUS.WEIGHT'), `${this.weight ? convertWeightForDisplaying(this.weight, this.weightUnit) : '-'} ${this.$t(`UNITS.${this.weightUnit.toUpperCase()}`)}`)}
      {this.renderInfo(h, 'brand/usual-training-time', this.$t('PAGE.MEMBERS.STATUS.TRAINING_TIME'), this.member.usualStartTimes.join(' - ') || '-')}
    </div>
  }

  renderInfo (h: CreateElement, icon, title, value) {
    return <div class='details-targets-card'>
      <Icon name={icon} />
      <Subtitle1>{title}</Subtitle1>
      <Subtitle1 class='value'>{value}</Subtitle1>
    </div>
  }

  created () {
    EventBus.on('reloadHealthGoal', this.loadCurrentGoal)

    return Promise.all([this.loadCurrentGoal(), this.loadBodyMeasurements()])
  }

  beforeDestroy () {
    EventBus.off('reloadHealthGoal', this.loadCurrentGoal)
  }

  protected loadCurrentGoal () {
    return this.apiClient.get(API.TRAINERS_API.HEALTH_GOAL.replace('{memberId}', this.$route.params.id))
      .then((response: HealthGoalResponse) => {
        if (response.health_goal) this.goal = response.health_goal.name || this.$t(`PAGE.MEMBERS.GOALS.${response.health_goal.key}`)
      })
      .catch((error: ApiCallError) => {
        if (error.status === 404) {
          this.goal = ''
          return Promise.resolve()
        }

        console.error('failed to retrieve health goal', error)
        return Promise.reject(error)
      })
  }

  protected loadBodyMeasurements () {
    return this.apiClient.get<BodyMeasurementsResponse>(API.TRAINERS_API.BODY_MEASUREMENTS.replace('{memberId}', this.$route.params.id))
      .then((response) => {
        const bodyMeasurements = new BodyMeasurements(camelcaseKeys(response.profile, { deep: true }))
        this.height = bodyMeasurements.metrics['height'] ? bodyMeasurements.metrics['height'].slice().sort((a, b) => a.effectiveAt > b.effectiveAt ? -1 : a.effectiveAt < b.effectiveAt ? 1 : 0)[0].value : null
        this.weight = bodyMeasurements.metrics['body_weight'] ? bodyMeasurements.metrics['body_weight'].slice().sort((a, b) => a.effectiveAt > b.effectiveAt ? -1 : a.effectiveAt < b.effectiveAt ? 1 : 0)[0].value : null
      })
      .catch((error) => console.error('failed to retrieve body measurements', error))
  }
}
